import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import type {
  GetTeamRankingsToolInput,
  GetTeamRankingsToolOutput,
  RankedTeammate,
} from "@repo/app-types/agents/getTopTeammatesTool";
import {
  ChatToolBase,
  ChatToolDisclosure,
  type ToolComponent,
} from "@repo/ui/chat/chatToolCall";
import { IconTrophy } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { formatCriterionScore, formatRate } from "./playbookScoreFormatting.js";
import { SelectedPlaybookSection } from "./selectedPlaybookSection.js";

export const GetTeamRankingsTool: ToolComponent<
  GetTeamRankingsToolInput,
  GetTeamRankingsToolOutput
> = (props) => {
  const runningLabel = props.input.criterion_id
    ? "Ranking the team by criterion score"
    : "Ranking the team by playbook score";

  if (props.status.type === "running") {
    return <ChatToolBase status={props.status}>{runningLabel}</ChatToolBase>;
  }

  if (checkIsErrorToolOutput(props.output)) {
    return (
      <ChatToolBase status={props.status}>
        An error occurred while ranking the team
      </ChatToolBase>
    );
  }

  if (props.status.type === "complete" && props.output) {
    const { teammates } = props.output;
    return (
      <ChatToolDisclosure
        status={props.status}
        icon={
          <IconTrophy className="mt-0.5 size-3.5 shrink-0 text-indigo-500" />
        }
        label={`Ranked ${formatTeammateCount(teammates.length)}`}
      >
        <div className="space-y-3">
          <SelectedPlaybookSection playbook={props.output.playbook} />
          <TeamRankings result={props.output} />
        </div>
      </ChatToolDisclosure>
    );
  }

  return <ChatToolBase status={props.status}>{runningLabel}</ChatToolBase>;
};

const TeamRankings: FC<{ result: GetTeamRankingsToolOutput }> = ({
  result,
}) => {
  const { teammates } = result;

  if (teammates.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        No teammates cleared the minimum call volume.
      </div>
    );
  }

  const criterionAverage = teammates
    .map((teammate) => teammate.criterion_average)
    .find((average) => average !== null);
  const rankedByCriterion = criterionAverage !== undefined;
  const hasWinRate = teammates.some((teammate) => teammate.win_rate !== null);

  // Tailwind only sees literal class strings, so pick a full grid template by
  // how many optional columns (win rate, criterion) are present rather than
  // building the arbitrary value dynamically.
  const extraColumns = (hasWinRate ? 1 : 0) + (rankedByCriterion ? 1 : 0);
  const rowGrid =
    extraColumns === 2
      ? "grid grid-cols-[minmax(0,1fr)_3.5rem_3.5rem_3.5rem] items-baseline gap-2"
      : extraColumns === 1
        ? "grid grid-cols-[minmax(0,1fr)_3.5rem_3.5rem] items-baseline gap-2"
        : "grid grid-cols-[minmax(0,1fr)_3.5rem] items-baseline gap-2";

  return (
    <div className="space-y-1.5 text-xs text-gray-600">
      <div
        className={twMerge(
          rowGrid,
          "text-[10px] font-semibold tracking-wide text-gray-400 uppercase",
        )}
      >
        <div>Teammate</div>
        <div className="text-right">Score</div>
        {hasWinRate ? <div className="text-right">Win</div> : null}
        {rankedByCriterion ? (
          <div className="truncate text-right">
            {criterionAverage?.criterion_name ?? "Criterion"}
          </div>
        ) : null}
      </div>
      {teammates.map((teammate) => (
        <div key={teammate.teammate_id} className={rowGrid}>
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">
              {teammate.teammate_name ?? "Unknown teammate"}
            </div>
            <div className="truncate text-muted-foreground">
              {formatCallCount(teammate.call_count)}
            </div>
          </div>
          <div className="text-right text-foreground tabular-nums">
            {formatRate(teammate.playbook_score_average)}
          </div>
          {hasWinRate ? (
            <div className="text-right text-foreground tabular-nums">
              {formatRate(teammate.win_rate)}
            </div>
          ) : null}
          {rankedByCriterion ? (
            <div className="text-right text-foreground tabular-nums">
              {formatCriterionAverage(teammate)}
            </div>
          ) : null}
        </div>
      ))}
      <CompanyMedians result={result} showWinRate={hasWinRate} />
    </div>
  );
};

const CompanyMedians: FC<{
  result: GetTeamRankingsToolOutput;
  showWinRate: boolean;
}> = ({ result, showWinRate }) => {
  const { company_playbook_score_median, company_win_rate_median } = result;
  if (
    company_playbook_score_median === null &&
    company_win_rate_median === null
  ) {
    return null;
  }
  return (
    <div className="pt-1 text-[11px] text-muted-foreground">
      {`Company median: ${formatRate(company_playbook_score_median)} playbook score`}
      {showWinRate ? ` · ${formatRate(company_win_rate_median)} win rate` : ""}
    </div>
  );
};

function formatCriterionAverage(teammate: RankedTeammate): string {
  const average = teammate.criterion_average;
  if (average === null) {
    return "—";
  }
  return formatCriterionScore(
    average.average_score,
    average.grading_value_type,
  );
}

function formatTeammateCount(count: number): string {
  return count === 1 ? "1 teammate" : `${count} teammates`;
}

function formatCallCount(count: number): string {
  return count === 1 ? "1 call" : `${count} calls`;
}
